<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toggleActions } from "@/action/toggleActions";
import { userActionsMenuOpened, userMenuStatusLinkClicked } from "@/analytics/events/menu";
import { trackEvent } from "@/analytics/track";
import { getAlmInfo } from "@/backend/read/BackendReader";
import { action } from "@/components/action/action";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import InfoMarker from "@/components/ui/InfoMarker/InfoMarker.vue";
import InfoMessage from "@/components/ui/InfoMessage/InfoMessage.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSeparator from "@/components/ui/MenuList/MenuListSeparator.vue";
import UserAvatar from "@/components/ui/UserAvatar/UserAvatar.vue";
import {
  cockpitBillingUrl,
  cockpitProfileUrl,
  cockpitSessionUrl,
} from "@/components/utils/cockpitUrls";
import { useBoardStore } from "@/store/board";
import { useLicenseStore } from "@/store/license";
import { useMessengerStore } from "@/store/messenger";
import { useSessionStore } from "@/store/session";
import { useUserStore } from "@/store/user";

const route = useRoute();
const router = useRouter();

const cockpitUrl = computed(() => cockpitSessionUrl({ boardType: useBoardStore().board?.type }));

const currentUser = computed(() => useUserStore().currentUser);
const cockpitAllowed = computed(() => useUserStore().isAllowed("cockpit"));
const showUserSync = computed(() => useSessionStore().userSync === "unsynced");
const almType = computed(() => getAlmInfo().name);

const { t } = useI18n();
const expirationMessage = computed(() => {
  if (useLicenseStore().isExpired) {
    return t("license.expired");
  }
  const expiresIn = useLicenseStore().willExpireIn;
  if (expiresIn) {
    return t("license.expiredInDays", expiresIn);
  }
  return "";
});
const showExpirationMessage = computed(() => expirationMessage.value && cockpitAllowed.value);

const intercomEnabled = computed(() => useMessengerStore().show && window.Intercom);

/**
 * Open the News page in Intercom, or the release notes if Intercom isn't enabled
 */
function openNews() {
  if (intercomEnabled.value) {
    window.Intercom("showSpace", "news");
  } else {
    window.open("https://www.piplanning.io/releases", "_blank");
  }
}

const handleDropdownOpen = () => {
  trackEvent(userActionsMenuOpened());
};

function trackStatusLinkClicked() {
  trackEvent(userMenuStatusLinkClicked());
}

function openExternal(url: string) {
  window.open(url, "_blank");
}
</script>

<template>
  <DropdownMenu
    v-if="currentUser"
    :offset="{ top: 8, left: -10 }"
    max-height="auto"
    @open="handleDropdownOpen"
  >
    <template #trigger="{ isOpen }">
      <BaseTooltip position="bottom">
        <BaseButton
          id="user-actions-trigger"
          variant="ghost"
          color="grey"
          style="position: relative"
          data-dropdown-trigger
          :aria-label="$t('userAction.mainMenu')"
          :aria-expanded="isOpen"
          aria-haspopup="menu"
          aria-controls="user-actions-menu"
        >
          <UserAvatar :user="currentUser" size="big" />
          <InfoMarker
            v-if="showUserSync || showExpirationMessage"
            level="error"
            :top="0"
            :right="9"
          />
        </BaseButton>
        <template #content>
          {{ $t("userAction.mainMenu") }}
        </template>
      </BaseTooltip>
    </template>
    <MenuList>
      <MenuListItems id="user-actions-menu" class="dropdown" role="menu">
        <div class="info">
          <p v-if="currentUser.name">{{ currentUser.name }}</p>
          <p class="email">{{ currentUser.email }}</p>
        </div>
        <div v-if="showUserSync" class="warning">
          <InfoMessage level="warn">
            {{ $t("userAction.userNotSynced", { almType }) }}
            <a :href="cockpitProfileUrl(route.fullPath)">
              {{ $t("userAction.gotoProfileSync") }}
            </a>
          </InfoMessage>
        </div>
        <div v-if="showExpirationMessage" class="warning">
          <InfoMessage level="warn">
            {{ expirationMessage }}
            <a target="_blank" :href="cockpitBillingUrl()">
              {{ $t("license.renew") }}
            </a>
          </InfoMessage>
        </div>
        <MenuListSeparator />
        <MenuListItem
          icon="persona/user"
          :text="$t('userAction.profile')"
          :aria-label="$t('label.mainMenu.profile')"
          @click="openExternal(cockpitProfileUrl())"
        />
        <MenuListItem
          v-if="cockpitAllowed"
          icon="general/rte-cockpit"
          :text="$t('userAction.rteCockpit')"
          :aria-label="$t('label.mainMenu.rteCockpit')"
          @click="openExternal(cockpitUrl)"
        />
        <MenuListSeparator />
        <MenuListItem v-bind="action(toggleActions.showSettings)" :shortcut="null" />
        <MenuListItem v-bind="action(toggleActions.showShortcuts)" :shortcut="null" />
        <MenuListItem v-bind="action(toggleActions.showIntercom)" />
        <MenuListSeparator />
        <MenuListItem
          :text="$t('userAction.documentation')"
          :aria-label="$t('label.mainMenu.documentation')"
          @click="openExternal('https://help.piplanning.io')"
        />
        <MenuListItem
          :text="$t('userAction.releaseNotes')"
          :aria-label="$t('label.mainMenu.releaseNotes')"
          @click="openNews"
        />
        <MenuListItem
          :text="$t('userAction.tutorials')"
          :aria-label="$t('label.mainMenu.tutorials')"
          @click="openExternal('https://www.piplanning.io/tutorials')"
        />
        <MenuListItem
          :text="$t('page.status')"
          @click="
            trackStatusLinkClicked();
            router.push('/status?referred');
          "
        />
        <MenuListSeparator />
        <MenuListItem
          icon="general/logout"
          :text="$t('userAction.logOut')"
          @click="router.push({ path: '/logout', query: { reason: 'user-invoked' } })"
        />
      </MenuListItems>
    </MenuList>
  </DropdownMenu>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables";
@use "@/styles/variables/colors";

#user-actions-menu {
  max-width: 22em;

  .info {
    font-weight: font.$weight-bold;
    padding: 8px 12px;

    p {
      margin: 0;
    }

    .email {
      font-weight: font.$weight-light;
      color: colors.$text-subtle;
      text-transform: none;
    }
  }

  .warning {
    padding: 2px 4px 8px;
  }
}
</style>
